import { BetModel } from '@bpos/common';
import { Bet, BetSlip } from '@bpos/v2/bet-placement';
import { BposOddsConverterService, OddsOperations } from '@frontend/sports/odds/feature';
import { UserOddsFormatSettingsService } from '@frontend/sports/user/feature';

import { IBetslipState } from '../../../../../base/store/state';
import { BetslipPick } from '../../../../../core/picks/betslip-pick';
import { IBasePrice, IBoostedPrice, PickType } from '../../../../../core/picks/pick-models';
import { ISlipPick } from '../../../../bet-generation/models';
import { IBetPicks } from '../../../models';
import { IPickRequestMapper } from './pick-request.models';

export abstract class BasePickRequestMapper implements IPickRequestMapper {
    constructor(
        private bposOddsConverterService: BposOddsConverterService,
        private userOddsFormatSettingsService: UserOddsFormatSettingsService,
    ) {}

    appendBetDetails(
        betslip: BetSlip,
        slipPick: ISlipPick,
        index: number,
        requestedPicks: IBetPicks,
        state?: IBetslipState | undefined,
        options?: { isTeaser?: boolean; isInGroup?: boolean },
    ): { nextIndex: number } {
        const bet = this.createBetFromPick(index, slipPick.pick, slipPick.price, slipPick.isBanker, !!options?.isInGroup);
        this.mapBetDetails(slipPick, bet, slipPick.pick.boostedPriceDetails, state, options);

        betslip.bets.push(bet);
        requestedPicks.picks[index] = slipPick.pick;

        return { nextIndex: index + 1 };
    }

    private createBetFromPick(betIndex: number, betslipPick: BetslipPick, price: IBasePrice, isBanker: boolean, isInGroup: boolean): Bet {
        const priceBoost = betslipPick.boostedPriceDetails;

        return {
            index: betIndex,
            isBanker,
            betModel: this.getBetModel(betslipPick),
            odds: priceBoost?.isPriceBoosted
                ? this.bposOddsConverterService.convertToBposOdds(priceBoost.boostedPrice?.nativeOdds!)
                : this.bposOddsConverterService.convertToBposOdds(price.nativeOdds),
            oddsFormat: OddsOperations.convertToBposOddsFormat(this.userOddsFormatSettingsService.oddsFormat),
            additionalInformation: { informationItems: [] },
            picks: [],
            betDetails: [],
            priceBoostDetails:
                !isInGroup && priceBoost?.isPriceBoosted
                    ? {
                          originalOdds: this.bposOddsConverterService.convertToBposOdds(price.nativeOdds),
                          originalPickId: betslipPick.acceptedPrice.price?.id,
                          pickReferenceId: priceBoost.boostedPriceId!.toString(),
                      }
                    : undefined,
        } as unknown as Bet;
    }

    private getBetModel(pick: BetslipPick): BetModel {
        switch (pick.id.getPickType()) {
            case PickType.V1Pick:
                return BetModel.Result;
            case PickType.V2ParticipantPick:
                return BetModel.Participant;
            case PickType.V2OptionMarketPick:
            case PickType.BetBuilderPick:
            case PickType.V2OptionMarketXcastPick:
            case PickType.GroupedPicks:
                return BetModel.Option;
        }
    }

    abstract mapBetDetails(
        slipPick: ISlipPick,
        bet: Bet,
        boostedPriceDetails?: IBoostedPrice,
        state?: IBetslipState,
        options?: { isTeaser?: boolean; isInGroup?: boolean },
    ): void;
}
